<script lang="ts" setup>
    import { useScroll } from '@vueuse/core';
    import type { Product, ProductPreview } from '~/graphql/generated';

    interface ProductTileProps {
        product: Partial<Product | ProductPreview>;
        /**
         * Fine-tune the size of the actual image where the product tile is used
         *
         * default: `100vw md:470 lg:300 xl:382`
         */
        imageSizes?: string;
        event_name?: string;
    }
    const props = withDefaults(defineProps<ProductTileProps>(), {
        imageSizes: '100vw md:470 lg:300 xl:382',
    });

    const MAX_COLORS = 5;

    const {
        addToCartDisabled,
        bikeDetail,
        changeHoverColorOption,
        colorOption,
        colorOptionChanged,
        config,
        handleAddToCart,
        hoverColorVariant,
        isRedCharity,
        product,
        selectedColor,
        toURL,
        variant,
    } = useProductCard(props.product, { disableVariantStockRedirect: true });
    const { isUS } = useLocales();

    const imageWrapper = ref<HTMLElement | null>(null);
    const { x, isScrolling } = useScroll(imageWrapper);
    const imageIndex = ref(0);
    watch(isScrolling, (isScrollingNew) => {
        if (isScrollingNew || !imageWrapper.value) return;
        const imageWrapperWidth = imageWrapper.value.scrollWidth;
        imageIndex.value = x.value < imageWrapperWidth / 2 ? 0 : 1;
    });

    const sku = computed(() => hoverColorVariant.value?.sku || variant.value?.sku);
    const primaryImage = computed(() => (sku.value ? getAssetUrl(`${sku.value}-side`) : ''));
    const secondaryImage = computed(() => (sku.value ? getAssetUrl(`${sku.value}-perspective`) : ''));

    const colorOptions = computed(() => {
        if (!colorOption.value?.stringValues || !selectedColor.value) return;
        return colorOption.value.values.slice(0, MAX_COLORS);
    });
    const additionalColors = computed(() => {
        if ((colorOption.value?.values?.length ?? 0) <= MAX_COLORS) return;
        return colorOption.value?.values?.length ?? 0 - MAX_COLORS;
    });
    const disableAddToCart = computed(() => (config?.optionsRendarable?.length ?? 0) > Number(Boolean(colorOption.value)));

    const secondaryImageError = ref(false);
    const handleSecondaryImageError = () => {
        secondaryImageError.value = true;
    };
</script>
<template>
    <div class="mol-product-tile h-full">
        <div
            class="tile relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-woom-grey-mid bg-white ring-1 ring-transparent transition-all duration-300 focus-within:ring-woom-black hover:border-opacity-0 hover:ring-woom-black">
            <nuxt-link
                :to="toURL"
                :data-item-id="product.id"
                :data-item-name="product.title"
                class="tile-link gtm-click-product-listing group relative bg-woom-white-smoke py-5 outline-none">
                <div
                    class="no-scrollbar flex aspect-3/2 snap-mandatory overflow-scroll scroll-smooth outline-none max-xl:snap-x lg:overflow-hidden"
                    ref="imageWrapper">
                    <div
                        v-if="primaryImage"
                        :class="[
                            'flex aspect-3/2 h-full w-full snap-start transition-opacity duration-300 ease-in-out-smooth max-xl:snap-center',
                            { 'lg:group-hover:opacity-0': !secondaryImageError },
                        ]">
                        <atm-image
                            class="swiper-no-swiping flex h-full w-full justify-center"
                            :sizes="imageSizes"
                            :src="primaryImage"
                            :alt="removeWoomTextFormat(product.formattedTitle)" />
                    </div>
                    <!-- Load secondary image only client side to make sure load error is handled correctly -->
                    <client-only>
                        <div
                            v-if="secondaryImage && !secondaryImageError"
                            class="flex aspect-3/2 w-full shrink-0 snap-start transition-opacity duration-300 ease-in-out-smooth max-xl:snap-center lg:absolute lg:opacity-0 lg:group-hover:opacity-100">
                            <atm-image
                                class="swiper-no-swiping flex h-full w-full justify-center"
                                :sizes="imageSizes"
                                :src="secondaryImage"
                                :alt="removeWoomTextFormat(product.formattedTitle)"
                                role="presentation"
                                @error="handleSecondaryImageError" />
                        </div>
                    </client-only>
                </div>
                <div
                    v-if="!secondaryImageError"
                    class="absolute bottom-4 flex w-full justify-center gap-2 lg:hidden">
                    <div
                        v-for="(_, index) in Array(2)"
                        :key="index"
                        :class="['h-2 w-2 rounded-full transition-colors', index === imageIndex ? 'bg-woom-black' : 'bg-woom-grey-mid']"></div>
                </div>
                <lazy-mol-product-labels
                    v-if="product.labels && variant?.labels"
                    :labels="product.labels"
                    :variant-labels="variant.labels"
                    class="left-4 top-4 mr-4" />
                <lazy-woom-icon-red-charity
                    v-if="isRedCharity"
                    class="absolute right-4 top-4 h-6 fill-woom-red" />
            </nuxt-link>
            <div class="space-y-4 pt-3">
                <div class="flex flex-wrap items-center gap-2 px-2 lg:gap-1">
                    <lazy-mol-color-options
                        v-if="colorOptions"
                        :options="colorOptions"
                        @hover="changeHoverColorOption"
                        @changed="colorOptionChanged"
                        class="!contents" />
                    <nuxt-link
                        v-if="additionalColors"
                        :to="toURL"
                        class="relative flex h-10 w-10 items-center justify-center rounded-full border-2 border-black border-opacity-0 text-sm transition duration-200 ease-out will-change-transform hover:scale-110 hover:border-opacity-100 focus:scale-110 lg:h-9 lg:w-9">
                        <span>+{{ additionalColors }}</span>
                        <span class="sr-only">{{ $t('product.colors', additionalColors) }}</span>
                    </nuxt-link>
                </div>
                <div :class="['swiper-no-swiping px-4 text-sm', isUS ? 'h-16' : 'h-10']">
                    <div :class="{ 'font-bold': !hasWoomTransformer(product.formattedTitle) }">
                        <renderer-html :content="woomTextFormat(product.formattedTitle)" />
                    </div>
                    <div class="line-clamp-1">
                        <lazy-atm-product-card-bike-details
                            v-if="bikeDetail"
                            :bike-detail="bikeDetail" />
                        <renderer-html
                            v-else
                            :content="woomTextFormat(product.description)" />
                    </div>
                    <atm-stamped-stars
                        :to="toURL + '#reviews'"
                        :data-id="product.productId" />
                </div>
                <div class="swiper-no-swiping flex items-center justify-between border-t border-woom-grey-mid pl-4 text-sm">
                    <atm-price
                        :price="variant?.price"
                        :compare-at-price="variant?.compareAtPrice" />
                    <button
                        v-if="!disableAddToCart && canVariantQuickSell(product, variant?.id)"
                        :aria-label="$t('cta.cart.add')"
                        :class="[
                            'm-px flex h-12 w-12 items-center justify-center rounded-full lg:h-10 lg:w-10',
                            { 'hover:text-woom-red focus:text-woom-red': !addToCartDisabled },
                        ]"
                        :disabled="addToCartDisabled"
                        @click="() => handleAddToCart()">
                        <woom-icon-add-to-cart class="h-5 w-5 fill-current transition-colors" />
                    </button>
                    <nuxt-link
                        v-else
                        :to="toURL"
                        :aria-label="removeWoomTextFormat(product.formattedTitle)"
                        class="group m-[2px] flex h-12 w-12 items-center justify-center rounded-full hover:text-woom-red focus:text-woom-red lg:h-10 lg:w-10">
                        <!-- <span class="h-6 w-6 rounded-full bg-woom-black p-1 transition-colors group-hover:bg-black group-focus:bg-black">
                            <woom-icon-plus class="fill-white" />
                        </span> -->
                        <woom-icon-plus-circle class="h-6 w-6 fill-current transition-colors" />
                    </nuxt-link>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
    .mol-product-tile {
        container: product-tile / inline-size;
    }

    /* This throws an error => @container product-tile screen(md) { */
    @container product-tile (min-width: 768px) {
        .tile {
            @apply flex-row items-center justify-evenly;
            > * {
                @apply w-full max-w-md;
            }
        }
        .tile-link {
            @apply bg-white py-10;
        }
    }
</style>
